/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import BlackOpenChevron from '../images/homepage-refresh/black-open-chevron.svg';
import RedCloseChevron from '../images/homepage-refresh/red-close-chevron.svg';
import { fixPropWarnings } from '../utils/fixPropWarnings';

import InlineLink from './InlineLink';
import ButtonOrLink from './ButtonOrLink';
import BladeController from './BladeController';
import OutsideClickHandler from './OutsideClickHandler';

const Dropdowns = styled.div`
  flex: 1 0 auto;
  display: none;

  @media only screen and (min-width: 1120px) {
    &:lang(en) {
      display: inline-flex;
      align-items: center;
    }
  }

  @media only screen and (min-width: 1440px) {
    &:lang(fr) {
      display: inline-flex;
      align-items: center;
    }
  }
`;

interface DropdownProps {
  isActive: boolean;
  productMenu?: boolean;
}

const Dropdown = styled.div.withConfig(fixPropWarnings('isActive'))<DropdownProps>`
  position: relative;
  background-color: ${({ isActive }) => (isActive ? '#ffffff' : 'transparent')};
  border-radius: 6px 6px 0px 0px;
  padding: 10px;
  margin: 0 2.5px;

  filter: ${({ isActive }) => isActive ? 'drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.1))' : 'none'};

  &:lang(fr) {
    padding: 10px 8px;
  }

  &:first-child {
    margin: 0px 2.5px 0px 0px;
  }

  &::after {
    content: '';
    height: 6px;
    width: 6px;
    display: block;
    position: absolute;
    left: 100%;
    bottom: 0;

    background: radial-gradient(
      180% 180% at right -65% top -65%,
      transparent 99%,
      #ffffff
    );
  }
`;

const DropdownBtn = styled.button.withConfig(fixPropWarnings('isActive'))<DropdownProps>`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: 400;
  font-size: 18px;
  line-height: 30px;
  color: ${({ isActive }) => (isActive ? `#E1251B` : '#2B2C32')};

  &::after {
    content: ${({ isActive }) => isActive ? `url(${RedCloseChevron})` : `url(${BlackOpenChevron})`};
    position: relative;
    top: -3px;
    margin: 0 0 0 10px;
  }
`;

const NavLinkStyles = css`
  background: #ebedeb;
  border-radius: 4px;
  padding: 10px;
  font-size: 14px;
  line-height: 17px;
  white-space: nowrap;
  margin: 6px 0;
  min-width: 180px;
  height: 100%;
  text-align: left;
  transition: background-color 200ms ease, color 200ms ease;

  &:hover,
  &:focus {
    font-weight: 600;
    opacity: 1;
    background: ${themeGet('colors.velvet')};
    color: ${themeGet('colors.white')};
  }

  span {
    text-decoration: none;
  }

  &:first-child {
    margin: 0 0 6px 0;
  }

  &:last-child {
    margin: 6px 0 0 0;
  }
`;

const ProductText = styled.div`
  background: #2b2c32;
  padding: 4px 20px;
  min-height: 44px;
  transition: background-color 200ms ease, color 200ms ease;

  p {
    margin: 0;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: ${themeGet('colors.white')};
    text-align: center;
  }
`;

const ProductImg = styled(GatsbyImage)`
  transition: all 300ms ease;
  object-fit: contain;
  width: 100%;
`;

const DropdownProductLinkWrapper = styled(ButtonOrLink)`
  &:hover,
  &:focus {
    ${ProductText} {
      background: ${themeGet('colors.velvet')};

      p {
        font-weight: 600;
        color: ${themeGet('colors.white')};
      }
    }

    ${ProductImg} {
      transform: scale(1.2);
    }
  }
`;

const DropdownProductLink = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background-color: #f7f7f7;
  display: flex;
  flex-direction: column;
  margin: 0 6px;
  min-width: 134px;
  height: 100%;
  filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.1));
`;

const ProductContainer = styled.div`
  background-color: ${themeGet('colors.white')};
  padding: 8px 4px 6px;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavLink = styled(InlineLink)`
  ${NavLinkStyles}
`;

const NavBladeLink = styled(BladeController)`
  ${NavLinkStyles}
`;

const DropdownMenu = styled.div.withConfig(fixPropWarnings('isActive', 'productMenu'))<DropdownProps>`
  z-index: 10;
  position: absolute;
  top: calc(100% - 1px);
  left: 0;
  min-width: 115%;

  display: ${({ isActive }) => (isActive ? 'inline-flex' : 'none')};
  background-color: ${themeGet('colors.white')};
  border-radius: 0px 12px 12px 12px;
  padding: 24px 20px;

  flex-direction: ${({ productMenu }) => (productMenu ? 'row' : 'column')};

  ${NavLink} {
    background: ${({ isActive }) => isActive ? `${themeGet('colors.velvet')}` : '#ebedeb'};
  }
`;

interface DropdownNavigationItemProps {
  id: string;
  contentful_id: string;
  heading: string;
  links: Array<any>;
  dropdowns: Array<string>;
  openDropdown: (dropdowns: any) => void;
  onNavItemTabbed: (e: any) => void;
  handleKeyDown: (e: any, isLast: boolean) => void;
}

const DropdownNavigationItem = ({
  id,
  contentful_id,
  heading,
  links,
  dropdowns,
  openDropdown,
  onNavItemTabbed,
  handleKeyDown,
}: DropdownNavigationItemProps) => {
  const filteredLinks = links.filter(
    (link) => !!(link?.internal?.type === 'ContentfulBlade' || link?.linkText),
  );

  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const isOpen = !!dropdowns.find((dropdownID) => id === dropdownID);
    setIsActive(isOpen);
  }, [dropdowns, id]);

  return (
    <Dropdown key={id} isActive={isActive}>
      <DropdownBtn
        onClick={() => (isActive ? openDropdown([]) : openDropdown([id]))}
        onKeyDown={onNavItemTabbed}
        isActive={isActive}
        aria-expanded={isActive}
        aria-controls={id}
      >
        {heading}
      </DropdownBtn>
      <DropdownMenu
        id={id}
        productMenu={contentful_id === '1QUpNUB2MTICvuL2tQn8EC'}
        isActive={isActive}
      >
        {filteredLinks.map((link, index) => {
          const isFirstEl = index === 0;
          const isLastEl = filteredLinks.length - 1 === index;

          const onKeyDown = isLastEl || isFirstEl
            ? (e: any) => handleKeyDown(e, isLastEl)
            : undefined;

          if (link?.internal?.type === 'ContentfulBlade') {
            return (
              <NavBladeLink key={link.id} data={link} onKeyDown={onKeyDown} />
            );
          }
          if (
            link?.linkText
            && link?.internal?.type === 'ContentfulLinkWrapper'
          ) {
            return link?.iconDamAsset ? (
              <DropdownProductLinkWrapper
                key={link.id}
                href={link.linkUrl}
                onKeyDown={onKeyDown}
              >
                <DropdownProductLink>
                  <ProductContainer>
                    {link.iconDamAsset.asset.localImage && (
                      <ProductImg
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        image={getImage(link.iconDamAsset.asset.localImage)}
                        objectFit="contain"
                        alt=""
                      />
                    )}
                  </ProductContainer>
                  <ProductText>
                    <p>{link.linkText}</p>
                  </ProductText>
                </DropdownProductLink>
              </DropdownProductLinkWrapper>
            ) : (
              <NavLink key={link.id} href={link.linkUrl} onKeyDown={onKeyDown}>
                {link.linkText}
              </NavLink>
            );
          }
          return null;
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

interface DropdownNavigationProps {
  navLinks: Array<any>;
}

const DropdownNavigation = ({ navLinks }: DropdownNavigationProps) => {
  const [dropdowns, setDropdowns] = useState([]);

  const handleKeyDown = (e: any, isLast: boolean) => e.keyCode === 9
    && (isLast ? !e.shiftKey : e.shiftKey)
    && setDropdowns([]);

  return (
    <OutsideClickHandler
      isActive={dropdowns.length >= 1}
      callback={() => setDropdowns([])}
    >
      <Dropdowns>
        {navLinks
          && navLinks.map(({ id, contentful_id, sectionHeading, links }) => (
            <DropdownNavigationItem
              key={id}
              id={id}
              contentful_id={contentful_id}
              heading={sectionHeading}
              links={links}
              dropdowns={dropdowns}
              openDropdown={setDropdowns}
              onNavItemTabbed={(e) => handleKeyDown(e, false)}
              handleKeyDown={handleKeyDown}
            />
          ))}
      </Dropdowns>
    </OutsideClickHandler>
  );
};

export default DropdownNavigation;
